import { useMutation, useQuery } from '@apollo/client';
import React, { createContext, useContext, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { DELETE_PRESCRIPTION, GET_CAREPLAN, GET_CAREPLAN_PRESCRIPTIONS } from '../Queries';
import { GET_PEER_REVIEW } from './SidePanelQueries';
import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import SidePanelItem from './SidePanelItem';
import { getSiteLateralityFromSiteSet, getSiteRepresentation, getPrescriptionsWithSiteGroups } from '../Common';
import { CAREPLAN_PAGES, NO_TEMPLATES_MESSAGE } from '../Constants';
import {
  PrescriptionSiteGroupType,
  PrescriptionSiteType,
  ServerPrescriptionType,
  ROPatientCarePlanRoute,
} from '../Interface';
import { Logger } from 'shared-components/utils';

const logger = new Logger('PrescriptionsSidePanel');

// Context for prescriptions data
interface PrescriptionsContextType {
  prescriptions: ServerPrescriptionType[];
  loading: boolean;
  careplanId: string;
  onPrescriptionDeleted?: (prescriptionName: string) => void;
  onPrescriptionDeleteFailed?: (prescriptionName: string) => void;
}

const PrescriptionsContext = createContext<PrescriptionsContextType | undefined>(undefined);

// Custom hook to use prescriptions context
const usePrescriptions = (): PrescriptionsContextType => {
  const context = useContext(PrescriptionsContext);
  if (!context) {
    throw new Error('usePrescriptions must be used within a PrescriptionsProvider');
  }
  return context;
};

const EmptyPrescription = (): JSX.Element => (
  <div className="sidepanel-template-contents">
    <div className={'sidepanel-message'}>
      <span>{NO_TEMPLATES_MESSAGE}</span>
    </div>
  </div>
);

const isSiteActive = (
  siteInput: PrescriptionSiteType,
  pathLocation: string,
  siteIdx: string,
  siteGroupIdx: string,
  prescriptionIdx: string,
): boolean => {
  const { prescriptions } = usePrescriptions();
  // Early return if no prescriptions or site input
  if (!prescriptions?.length || !siteInput) {
    return false;
  }

  const siteIdxNonNull = siteIdx || '0';
  const siteGroupIdxNonNull = siteGroupIdx || '0';
  const prescriptionIdxNonNull = prescriptionIdx || '0';

  // For Voluming page - check if siteGroupIdx matches prescription index
  if (pathLocation.includes(CAREPLAN_PAGES.VOLUMING) || pathLocation.includes(CAREPLAN_PAGES.SPECIAL_PHYSICS_CONSULT)) {
    const prescriptionIndex =
      prescriptionIdxNonNull === 'last' ? prescriptions.length - 1 : parseInt(prescriptionIdxNonNull);
    const prescription = prescriptions[prescriptionIndex];

    if (!prescription) {
      return false;
    }

    // Check if the site exists in any sitegroup of this prescription
    return prescription.sitegroupSet.some((sitegroup: PrescriptionSiteGroupType) =>
      sitegroup.siteSet.some((site: PrescriptionSiteType) => site.id === siteInput.id),
    );
  }

  // For Simulation page - check if siteGroupIdx matches sitegroup index
  if (pathLocation.includes(CAREPLAN_PAGES.SIMULATION)) {
    // Flatten all sitegroups with their cumulative index
    const allSitegroups = prescriptions.flatMap((prescription: ServerPrescriptionType) => prescription.sitegroupSet);

    // Find the sitegroup at the specified index
    const targetSitegroupIndex = parseInt(siteGroupIdxNonNull); // Convert to 0-based index
    const targetSitegroup = allSitegroups[targetSitegroupIndex];

    if (!targetSitegroup) {
      return false;
    }

    // Check if the site exists in this specific sitegroup
    return targetSitegroup.siteSet.some((site: PrescriptionSiteType) => site.id === siteInput.id);
  }

  // For Prescription page - check if siteIdx matches site index
  if (pathLocation.includes(CAREPLAN_PAGES.PRESCRIPTION)) {
    // Flatten all sites with their cumulative index
    const allSites = prescriptions
      .sort((a: ServerPrescriptionType, b: ServerPrescriptionType) => a.orderRanking - b.orderRanking)
      .flatMap((prescription: ServerPrescriptionType) =>
        prescription.sitegroupSet
          .flatMap((sitegroup: PrescriptionSiteGroupType) => sitegroup.siteSet)
          .sort((a: PrescriptionSiteType, b: PrescriptionSiteType) => a.orderRanking - b.orderRanking),
      );

    // Find the site at the specified index
    const targetSiteIndex = parseInt(siteIdxNonNull); // Convert to 0-based index
    const targetSite = allSites[targetSiteIndex];

    if (!targetSite) {
      return false;
    }

    return targetSite.id === siteInput.id;
  }

  return false;
};

const SiteItem = ({ site }: { site: PrescriptionSiteType }): JSX.Element => {
  const theme = useTheme();
  const match: {
    params: { id: string; careplanId: string; siteGroupIdx: string; prescriptionIdx: string; siteIdx: string };
  } = useRouteMatch();
  const { siteGroupIdx, prescriptionIdx, siteIdx } = match.params;
  const pathLocation = useLocation().pathname;
  const isActive = isSiteActive(site, pathLocation, siteIdx, siteGroupIdx, prescriptionIdx);

  return (
    <Tooltip arrow title={<div>{[getSiteRepresentation(site), getSiteLateralityFromSiteSet(site)].join(' ')}</div>}>
      <ListItem
        data-testid="sidepanel-site"
        sx={{
          borderLeft: isActive ? `4px solid ${theme.palette.primary.main}` : 'none',
          borderRadius: '0px !important',
        }}>
        <ListItemText primary={[getSiteRepresentation(site), getSiteLateralityFromSiteSet(site)].join(' ')} />
      </ListItem>
    </Tooltip>
  );
};

const Prescription = ({
  prescription,
  prescriptionIdx,
}: {
  prescription: ServerPrescriptionType;
  prescriptionIdx: number;
}): JSX.Element => {
  const theme = useTheme();
  const { careplanId, onPrescriptionDeleted, onPrescriptionDeleteFailed } = usePrescriptions();
  const pathLocation = useLocation().pathname;
  const [deletePrescription, { loading: isLoading }] = useMutation(DELETE_PRESCRIPTION, {
    refetchQueries: [
      { query: GET_CAREPLAN_PRESCRIPTIONS, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
      { query: GET_PEER_REVIEW, variables: { careplanId } },
    ],
    // Keep isLoading true until the refetched careplan lands. Without this the
    // button re-enables while the stale prescription row is still rendered,
    // letting a second click fire deletePrescription for an already-deleted id.
    awaitRefetchQueries: true,
  });
  const showDelete = pathLocation.includes(CAREPLAN_PAGES.TEMPLATE_SELECTION);
  const sites = prescription.sitegroupSet
    .map((sitegroup: PrescriptionSiteGroupType) => sitegroup.siteSet)
    .flat()
    .sort((a: PrescriptionSiteType, b: PrescriptionSiteType) => a.orderRanking - b.orderRanking);
  const deleteMessage = (prescription: ServerPrescriptionType): string => {
    return prescription.sitegroupSet
      .map((sitegroup: PrescriptionSiteGroupType) => sitegroup.siteSet)
      .flat()
      .sort((a: PrescriptionSiteType, b: PrescriptionSiteType) => a.orderRanking - b.orderRanking)
      .map((site: PrescriptionSiteType) => [getSiteRepresentation(site), getSiteLateralityFromSiteSet(site)].join(' '))
      .join(', ');
  };
  return (
    <List sx={{ padding: 0 }} data-testid="sidepanel-prescription-title">
      <ListItem sx={{ backgroundColor: theme.palette.action.selected }}>
        <ListItemText primary={`Prescription ${prescriptionIdx + 1}`} />
        {showDelete && (
          <ListItemIcon>
            <IconButton
              data-testid="delete-prescription"
              disabled={isLoading}
              onClick={() => {
                if (isLoading) return;
                const prescriptionName = deleteMessage(prescription);
                deletePrescription({ variables: { prescriptionId: prescription.id } })
                  .then(() => {
                    onPrescriptionDeleted?.(prescriptionName);
                  })
                  .catch((error) => {
                    logger.error('Prescription deletion failed:', error);
                    onPrescriptionDeleteFailed?.(prescriptionName);
                  });
              }}>
              {isLoading ? <CircularProgress size={24} /> : <DeleteIcon color="error" />}
            </IconButton>
          </ListItemIcon>
        )}
      </ListItem>
      {sites.map(
        (site: PrescriptionSiteType, index: number): JSX.Element => (
          <SiteItem site={site} key={`site-${index}`} />
        ),
      )}
    </List>
  );
};

const PrescriptionsList = (): JSX.Element => {
  const { prescriptions } = usePrescriptions();

  return (
    <>
      {prescriptions.map(
        (prescription: ServerPrescriptionType, index: number): JSX.Element => (
          <Prescription key={index} prescription={prescription} prescriptionIdx={index} />
        ),
      )}
    </>
  );
};

interface PrescriptionsSidePanelProps {
  onPrescriptionDeleted?: (prescriptionName: string) => void;
  onPrescriptionDeleteFailed?: (prescriptionName: string) => void;
}

const PrescriptionsSidePanel = ({
  onPrescriptionDeleted,
  onPrescriptionDeleteFailed,
}: PrescriptionsSidePanelProps = {}): JSX.Element => {
  const match = useRouteMatch<ROPatientCarePlanRoute>();
  const { careplanId } = match.params;
  const theme = useTheme();
  const { loading, data } = useQuery(GET_CAREPLAN_PRESCRIPTIONS, {
    variables: { careplanId },
  });

  if (loading) return <div>Loading prescriptions</div>;

  const prescriptions = getPrescriptionsWithSiteGroups(data?.prescriptionsByCareplan);
  const isEmptyPrescriptions = prescriptions.length === 0;

  if (isEmptyPrescriptions) return <></>;

  const contextValue: PrescriptionsContextType = {
    prescriptions,
    loading,
    careplanId,
    onPrescriptionDeleted,
    onPrescriptionDeleteFailed,
  };

  return (
    <PrescriptionsContext.Provider value={contextValue}>
      <SidePanelItem title="Prescriptions" topBorderColor={theme.palette.primary.main}>
        {isEmptyPrescriptions ? <EmptyPrescription /> : <PrescriptionsList />}
      </SidePanelItem>
    </PrescriptionsContext.Provider>
  );
};

export default PrescriptionsSidePanel;
